import { useListSSOConfigurations } from "@/queries/organization-queries";
import { BuiltinSSOConfigCard, CustomSSOConfigCard } from "@/routes/settings/login/SSOConfigCard";
import { CustomDomainCard } from "@/routes/settings/login/CustomDomainCard";
import { EnforceCustomDomainCard } from "@/routes/settings/login/EnforceCustomDomainCard";
import {
    SSOConfiguration_ProviderType,
    SSOConfigurationState,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { type FC } from "react";
import { Banner } from "@/components/flexkit/Banner";
import { Link } from "@/components/flexkit/Link";
import { useFlagCustomDomainEnabled } from "@/hooks/use-flag";
import { Heading3 } from "@/components/podkit/typography/Headings";

export const LoginAndSecurity: FC<{ organizationId: string; isEnterpriseTier?: boolean }> = ({
    organizationId,
    isEnterpriseTier = false,
}) => {
    return (
        <div className="flex flex-col gap-6">
            <div className="text-base text-content-secondary">Configure SSO for your organization.</div>

            {!isEnterpriseTier && (
                <Banner
                    variant="info"
                    className="py-4"
                    data-testid="free-tier-banner"
                    text={
                        <>
                            Upgrade to{" "}
                            <Link
                                to="/settings/manage-organization"
                                className="font-medium text-content-brand hover:underline"
                            >
                                Enterprise tier
                            </Link>{" "}
                            to manage login and security settings.
                        </>
                    }
                    action={{
                        text: "Upgrade now",
                        onClick: () => window.location.href = "/settings/manage-organization",
                        "data-tracking-id": "upgrade-enterprise-login-security"
                    }}
                />
            )}

            <DomainAndAccess organizationId={organizationId} editable={isEnterpriseTier} />

            <LoginMethods organizationId={organizationId} editable={isEnterpriseTier} />
        </div>
    );
};

const DomainAndAccess: FC<{ organizationId: string; editable: boolean }> = ({ organizationId, editable }) => {
    const { value: isCustomDomainEnabled } = useFlagCustomDomainEnabled();

    if (!isCustomDomainEnabled) {
        return null;
    }

    return (
        <div className="flex flex-col gap-4">
            <Heading3>Domain & Access</Heading3>
            <CustomDomainCard organizationId={organizationId} editable={editable} />
            <EnforceCustomDomainCard organizationId={organizationId} editable={editable} />
        </div>
    );
};

const LoginMethods: FC<{ organizationId: string; editable: boolean }> = ({ organizationId, editable }) => {
    const { data: ssoConfigs } = useListSSOConfigurations();
    const builtinConfigs = ssoConfigs?.filter(isBuiltin) || [];
    const singleCustomConfig = ssoConfigs ? ssoConfigs.find(isCustom) : undefined;
    const hasSeveralActive =
        (ssoConfigs?.filter((c) => c.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE)?.length || 0) > 1;
    const deactivateable = hasSeveralActive && editable;

    return (
        <div className="flex flex-col gap-4">
            <Heading3>Login Methods</Heading3>
            {builtinConfigs.map((config) => (
                <BuiltinSSOConfigCard
                    key={`built-in-config-${config.issuerUrl}`}
                    organizationId={organizationId}
                    config={config}
                    deactivateable={deactivateable}
                    editable={editable}
                />
            ))}
            <CustomSSOConfigCard
                organizationId={organizationId}
                config={singleCustomConfig}
                deactivateable={deactivateable}
                editable={editable}
            />
        </div>
    );
};

const isCustom = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.CUSTOM;
const isBuiltin = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.BUILTIN;
